import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import { formatDateWithOptionsBs } from "@/lib/format";
import { CaretLeft, CaretRight, CaretDown, X, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { submitMonriForm } from "@/lib/monri";
import {
  clearBookingDraft, readBookingDraft, restoreBookingDraft, writeBookingDraft,
} from "@/lib/booking-draft";
import {
  visibleSteps, nextStep, prevStep, canAdvance, stepAfterRejectedBooking, type BookingStep,
} from "@/lib/booking-flow";
import { buildBookRequestBody } from "@/lib/booking-request";
import { maxExtraMembers, normalizeMemberEmails } from "@/lib/session-members";
import { MemberEmailsField } from "@/components/session-members/MemberEmailsField";
import { companyFundingShares } from "@/lib/company-funding";
import type { CompanyFundingQuote } from "@/types/company";
import { useEmergencySlots } from "@/api-queries/emergency";
import { BookingSuccess } from "./BookingSuccess";
import { BookingStepper } from "./BookingStepper";
import { ServicePicker } from "./ServicePicker";
import { CalendarGrid, bookingToday } from "./CalendarGrid";
import { TimeSlots } from "./TimeSlots";
import { ModalityStep } from "./ModalityStep";
import { PaymentStep } from "./PaymentStep";
import { ConfirmStep } from "./ConfirmStep";
import { chargeDiffersFromList, fmtKm } from "./AppointmentTicket";
import { BookingAuthStep } from "./BookingAuthStep";
import { BookingSummary, type BookingSummaryData } from "./BookingSummary";
import type { PaymentMethodId, CompanyCreditOption } from "./PaymentStep";
import type { ServiceInfo } from "./types";
import { useAuth } from "@/hooks/useAuth";

const STEP_LABEL_KEY: Record<BookingStep, string> = {
  service: "booking.stepService",
  date: "booking.stepAppointment",
  modality: "booking.stepModality",
  payment: "booking.stepPayment",
  confirm: "booking.stepConfirmation",
};

const MY_COMPANY_CREDIT_KEY = ["me", "company-credit"] as const;

async function fetchMyCompanyCredit() {
  const res = await fetch("/api/me/company-credit");
  if (!res.ok) return null;
  return res.json() as Promise<{
    companyId: string;
    companyName: string;
    balance: number | null;
    discountPercent: number;
    billingModel: "credit" | "invoice";
    cofundingPercent: number;
  } | null>;
}

export function BookingCalendar({
  therapistId, therapistName, services, resolvedCountry, officeAddress,
  matchingResultToken, onClose,
}: {
  therapistId: string;
  therapistName: string;
  services: ServiceInfo[];
  resolvedCountry?: string | null;
  officeAddress?: string | null;
  matchingResultToken?: string | null;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  // Sarajevo calendar date, not browser midnight: the server checks lead time
  // and horizon against Sarajevo, so the grid must start from the same day.
  const today = useMemo(() => bookingToday(), []);

  const [step, setStep] = useState<BookingStep>("service");
  const [selectedService, setSelectedService] = useState<ServiceInfo | null>(null);
  const [selectedModality, setSelectedModality] = useState<"online" | "in_person" | null>(null);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethodId | null>(null);
  const [memberEmails, setMemberEmails] = useState<string[]>([]);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState<Record<string, unknown> | null>(null);
  // Card booking whose server-charged amount differs from what the client was
  // shown: the session exists, but we stop before Monri so they see the amount.
  const [pendingMonri, setPendingMonri] = useState<{ monri: Parameters<typeof submitMonriForm>[0]; amount: number } | null>(null);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const [desktopSummaryOpen, setDesktopSummaryOpen] = useState(false);
  const [justAuthenticated, setJustAuthenticated] = useState(false);
  const didRestoreDraft = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();

  const dateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    : null;

  // Emergency slots live inside the regular-lead window on their own dates, so
  // "selected date is an emergency date" fully determines the booking kind.
  const emergency = useEmergencySlots(therapistId);
  const emergencyDates = useMemo(
    () => new Set((emergency.data?.days ?? []).map((d) => d.date)),
    [emergency.data],
  );
  const isEmergency = !!dateStr && emergencyDates.has(dateStr);

  // Emergency sessions have no service: the wizard's service pick is ignored
  // and every display surface shows this synthetic one instead.
  const emergencyDisplayService: ServiceInfo | null = useMemo(() => {
    if (!isEmergency || emergency.data?.price == null) return null;
    const day = emergency.data.days.find((d) => d.date === dateStr);
    const slot = day?.slots.find((s) => s.time === selectedTime);
    const durationMinutes = slot
      ? (Number(slot.endTime.slice(0, 2)) * 60 + Number(slot.endTime.slice(3, 5))) -
        (Number(slot.time.slice(0, 2)) * 60 + Number(slot.time.slice(3, 5)))
      : 60;
    return {
      id: "emergency", name: t("emergency.sessionTitle"), description: "",
      durationMinutes, price: emergency.data.price, modality: "online",
    };
  }, [isEmergency, emergency.data, dateStr, selectedTime, t]);

  const displayService = isEmergency ? emergencyDisplayService : selectedService;

  const modalityNeeded = selectedService?.modality === "both";
  const ctx = { modalityNeeded: !!modalityNeeded, emergency: isEmergency };

  const effectiveModality: "online" | "in_person" =
    isEmergency
      ? "online"
      : selectedService?.modality === "both"
        ? (selectedModality ?? "online")
        : selectedService?.modality === "in_person"
          ? "in_person"
          : "online";

  const persistBookingDraft = useCallback(() => {
    // Emergency drafts are never persisted: the slots expire within hours and a
    // restore after the onboarding redirect would rebuild a regular booking.
    if (isEmergency) return;
    if (!selectedService || !dateStr || !selectedTime || !selectedPayment) return;
    try {
      writeBookingDraft(window.sessionStorage, {
        therapistId, serviceId: selectedService.id, date: dateStr, time: selectedTime,
        paymentMethod: selectedPayment, modality: effectiveModality, savedAt: Date.now(), memberEmails,
      });
    } catch { /* sessionStorage may be unavailable */ }
  }, [therapistId, selectedService, dateStr, selectedTime, selectedPayment, effectiveModality, isEmergency, memberEmails]);

  useEffect(() => {
    if (didRestoreDraft.current || selectedService || services.length === 0) return;
    didRestoreDraft.current = true;
    try {
      const restored = restoreBookingDraft(readBookingDraft(window.sessionStorage, therapistId), services, therapistId);
      if (!restored) return;
      setSelectedService(restored.service);
      setSelectedDate(restored.date);
      setCurrentMonth(restored.date.getMonth());
      setCurrentYear(restored.date.getFullYear());
      setSelectedTime(restored.time);
      setSelectedPayment(restored.paymentMethod);
      setSelectedModality(restored.modality);
      setMemberEmails(restored.memberEmails);
      setStep("confirm");
    } catch { /* ignore */ }
  }, [services, therapistId, selectedService]);

  useEffect(() => { persistBookingDraft(); }, [persistBookingDraft]);

  const { data: myCredit = null } = useQuery({
    queryKey: MY_COMPANY_CREDIT_KEY,
    queryFn: fetchMyCompanyCredit,
    staleTime: 60_000,
  });

  // Prijava na koraku potvrde: za zaposlenog firme se tek sada zna udio firme.
  // Booking odmah bi naplatio punu cijenu iako firma plaća dio ili sve, pa se
  // zaposleni vraća na plaćanje da vidi novu podjelu.
  async function handleAuthenticatedAtConfirm() {
    setJustAuthenticated(true);
    queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
    const credit = await queryClient.fetchQuery({ queryKey: MY_COMPANY_CREDIT_KEY, queryFn: fetchMyCompanyCredit, staleTime: 0 });
    if (credit) {
      setSelectedPayment(null);
      setStep("payment");
      toast.info(t("booking.payCompanyAfterLogin", { companyName: credit.companyName }));
      return;
    }
    void handleBook();
  }

  // What POST /api/sessions/book will actually charge for this service. The listed
  // price honours the region the client picked, the charge uses the connection
  // country (and, for company credit, the admin business country), so the client
  // must see the server's number before paying. Emergency slots have one fixed
  // price everywhere and need no quote.
  const quoteServiceId = !isEmergency ? selectedService?.id ?? null : null;
  const { data: priceQuote = null } = useQuery({
    queryKey: ["booking", "price-quote", quoteServiceId, user?.id ?? null],
    queryFn: async () => {
      const res = await fetch(`/api/sessions/price-quote?serviceId=${encodeURIComponent(quoteServiceId!)}`);
      if (!res.ok) return null;
      return res.json() as Promise<{ priceCharged: number; companyFunding: CompanyFundingQuote | null }>;
    },
    enabled: !!quoteServiceId,
    staleTime: 60_000,
  });
  const quotedCharge = isEmergency ? (displayService?.price ?? null) : (priceQuote?.priceCharged ?? null);

  const companyCreditOption: CompanyCreditOption | null = useMemo(() => {
    if (!myCredit || !displayService) return null;
    // The server quote uses the same country and helper the booking endpoint
    // charges with. Emergency (no country multiplier) and a missing quote fall
    // back to the shared helper on the displayed price.
    const quote = !isEmergency ? priceQuote?.companyFunding ?? null : null;
    const shares = quote
      ? { companyShare: quote.companyShare, clientShare: quote.clientShare }
      : (() => {
          const s = companyFundingShares(displayService.price, myCredit.discountPercent, myCredit.cofundingPercent);
          return { companyShare: s.companyShareFen / 100, clientShare: s.clientShareFen / 100 };
        })();
    const sufficient = myCredit.billingModel === "invoice" || (myCredit.balance ?? 0) >= shares.companyShare;
    return {
      companyName: myCredit.companyName,
      billingModel: myCredit.billingModel,
      balance: myCredit.balance,
      discountPercent: myCredit.discountPercent,
      cofundingPercent: myCredit.cofundingPercent,
      chargeAmount: shares.companyShare,
      clientShare: shares.clientShare,
      fullyCovered: shares.clientShare === 0,
      sufficient,
    };
  }, [myCredit, displayService, isEmergency, priceQuote]);

  useEffect(() => {
    if (step === "payment" && companyCreditOption?.fullyCovered && companyCreditOption.sufficient && selectedPayment === null) {
      setSelectedPayment("company_credit");
    }
  }, [step, companyCreditOption, selectedPayment]);

  // Bank transfer is rejected server-side for emergency bookings; drop a stale
  // pick if the user switches from a regular date to an emergency one.
  useEffect(() => {
    if (isEmergency && selectedPayment === "bank_transfer") setSelectedPayment(null);
  }, [isEmergency, selectedPayment]);

  const canGoPrev = currentYear > today.getFullYear() || (currentYear === today.getFullYear() && currentMonth > today.getMonth());
  const prevMonth = () => {
    if (currentMonth === 0) setCurrentYear((y) => y - 1);
    setCurrentMonth((m) => (m === 0 ? 11 : m - 1));
    setSelectedDate(null); setSelectedTime(null);
  };
  const nextMonth = () => {
    if (currentMonth === 11) setCurrentYear((y) => y + 1);
    setCurrentMonth((m) => (m === 11 ? 0 : m + 1));
    setSelectedDate(null); setSelectedTime(null);
  };

  function goNext() {
    if (!canAdvance(step, { service: selectedService?.id ?? null, time: selectedTime, modality: selectedModality, payment: selectedPayment })) return;
    if (step === "confirm") { void handleBook(); return; }
    setStep(nextStep(step, ctx));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function goBack() {
    // A held Monri form belongs to the selection it was created for; going back
    // to change anything must not resurface it on the next confirm step.
    setPendingMonri(null);
    setStep(prevStep(step, ctx));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Sufinansiranje se primjenjuje samo od sebe kad firma ima sredstava; zaposleni
  // plaća samo svoj dio karticom ili virmanom.
  const cofunding = companyCreditOption && !companyCreditOption.fullyCovered && companyCreditOption.sufficient
    ? companyCreditOption
    : null;

  async function handleBook() {
    if (!selectedService || !selectedDate || !selectedTime || !selectedPayment || !dateStr) return;
    // Server provjerava i email platioca; ovdje samo format, duplikati i broj.
    const members = normalizeMemberEmails(memberEmails, { maxExtra: maxExtraMembers(selectedService.maxMembers) });
    if (!members.ok) {
      toast.error(members.error);
      return;
    }
    setIsBooking(true);
    try {
      const res = await fetch("/api/sessions/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildBookRequestBody({
          therapistId, serviceId: selectedService.id, date: dateStr, time: selectedTime,
          paymentMethod: selectedPayment, modality: effectiveModality,
          matchingResultToken, emergency: isEmergency,
          companyFunding: cofunding !== null && selectedPayment !== "company_credit",
          memberEmails: members.emails,
        })),
      });
      if (res.status === 428) {
        await res.json().catch(() => ({}));
        persistBookingDraft();
        const url = new URL(window.location.href);
        url.searchParams.set("book", "1");
        navigate({ to: "/onboarding", search: { redirect: url.pathname + url.search, gate: "1" } });
        return;
      }
      if (res.status === 402) {
        await res.json().catch(() => ({}));
        toast.error(t("booking.payCompanyInsufficient"));
        await queryClient.invalidateQueries({ queryKey: ["me", "company-credit"] });
        // Potvrda bez odabranog načina plaćanja je prazna, pa nazad na plaćanje
        // gdje osvježeno stanje firme objašnjava novu podjelu.
        setSelectedPayment(null); setStep(stepAfterRejectedBooking(402));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        toast.error((err as { error?: string }).error || t("booking.bookingError"));
        setSelectedTime(null); setSelectedPayment(null); setStep(stepAfterRejectedBooking(409));
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || t("booking.bookingError"));
      }
      const data = await res.json();
      if (data.paymentMethod === "monri" && data.monri) {
        clearBookingDraft(window.sessionStorage, therapistId);
        // Kod sufinansiranja kartica naplaćuje samo dio zaposlenog.
        const charged = typeof data.clientPays === "number"
          ? data.clientPays
          : typeof data.priceCharged === "number" ? data.priceCharged : null;
        const expected = cofunding ? cofunding.clientShare : quotedCharge;
        if (charged !== null && (expected === null || chargeDiffersFromList(charged, expected))) {
          setPendingMonri({ monri: data.monri, amount: charged });
          return;
        }
        submitMonriForm(data.monri);
        return;
      }
      clearBookingDraft(window.sessionStorage, therapistId);
      setBookingResult(data);
      setBookingSuccess(true);
    } catch (err) {
      // TypeError je mrežna greška browsera ("Failed to fetch"), ne poruka servera.
      toast.error(err instanceof Error && !(err instanceof TypeError) && err.message ? err.message : t("booking.bookingErrorGeneric"));
    } finally {
      setIsBooking(false);
    }
  }

  if (bookingSuccess && displayService && selectedDate && selectedTime && selectedPayment) {
    return (
      <BookingSuccess
        therapistName={therapistName} service={displayService} date={selectedDate}
        dateStr={dateStr!} time={selectedTime} onClose={onClose} paymentMethod={selectedPayment!}
        bankDetails={bookingResult?.bankDetails as { bankName: string; accountNumber: string; recipientName: string; reference: string; amount: number; currency: string } | undefined}
        modality={effectiveModality} officeAddress={officeAddress}
      />
    );
  }

  const steps = visibleSteps(ctx);
  const activeIndex = steps.indexOf(step);
  const stepperItems = steps.map((s) => ({ key: s, label: t(STEP_LABEL_KEY[s]) }));

  const advanceOk = canAdvance(step, { service: selectedService?.id ?? null, time: selectedTime, modality: selectedModality, payment: selectedPayment });
  const isLast = step === "confirm";
  // On the confirm step the authenticated user finalises via the footer button.
  // When not yet authenticated, BookingAuthStep renders its own action instead.
  const showConfirmInFooter = isLast && (isAuthenticated || justAuthenticated) && !pendingMonri;
  const confirmLabel = isBooking
    ? t("booking.booking")
    : selectedPayment === "monri"
      ? t("booking.confirmAndPay")
      : t("booking.confirmAndReserve");
  const totalAmount = selectedPayment === "company_credit" && companyCreditOption
    ? companyCreditOption.chargeAmount
    : cofunding
      ? cofunding.clientShare
      : (quotedCharge ?? displayService?.price ?? null);
  const companyPayment = companyCreditOption && (selectedPayment === "company_credit" || cofunding)
    ? { companyName: companyCreditOption.companyName, chargeAmount: companyCreditOption.chargeAmount }
    : null;
  const clientChargeAmount = isEmergency ? null : cofunding ? cofunding.clientShare : quotedCharge;
  const total = totalAmount !== null ? fmtKm(totalAmount) : "—";

  const summary: BookingSummaryData = {
    service: displayService,
    dateLabel: selectedDate ? formatDateWithOptionsBs(selectedDate, { weekday: "short", day: "numeric", month: "long" }) : null,
    dateStr: dateStr ?? null,
    time: selectedTime,
    modalityLabel: displayService ? (effectiveModality === "online" ? t("booking.modalityOnline") : t("booking.modalityInPerson")) : null,
    paymentLabel: selectedPayment ? (selectedPayment === "monri" ? t("booking.payCard") : selectedPayment === "bank_transfer" ? t("booking.payTransfer") : t("booking.payCompany")) : null,
  };

  return (
    <div data-testid="booking-wizard" className="flex min-h-full flex-col rounded-2xl border bg-card">
      <div className="border-b px-4 py-3 sm:px-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[17px]" style={{ fontFamily: "var(--font-heading)", fontWeight: 500 }}>{t("booking.title")}</h3>
          {onClose && (
            <Button variant="ghost" size="icon-sm" onClick={onClose} className="text-muted-foreground"><X className="size-4" /></Button>
          )}
        </div>
        <BookingStepper steps={stepperItems} activeIndex={activeIndex} />
      </div>

      <div className="flex flex-1 flex-col gap-6 p-4 sm:p-5">
        <div>
          {step === "service" && (
            <>
              <h1 className="mb-4 text-2xl" style={{ fontFamily: "var(--font-heading)", fontWeight: 500 }}>{t("booking.serviceQuestion")}</h1>
              <ServicePicker
                services={services} resolvedCountry={resolvedCountry} selectedId={selectedService?.id ?? null}
                viewerRole={user?.role ?? null}
                onSelect={(s) => {
                  setSelectedService(s); setSelectedModality(null); setSelectedDate(null); setSelectedTime(null);
                  setMemberEmails([]);
                  setStep(nextStep("service", { modalityNeeded: s.modality === "both" }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </>
          )}

          {step === "date" && (
            <div className="grid gap-5 sm:grid-cols-[300px_1fr] sm:gap-7">
              <div className="rounded-2xl border p-4">
                <CalendarGrid
                  therapistId={therapistId} currentMonth={currentMonth} currentYear={currentYear} today={today}
                  selectedDate={selectedDate} canGoPrev={canGoPrev} emergencyDates={emergencyDates}
                  onPrevMonth={prevMonth} onNextMonth={nextMonth}
                  onSelectDate={(d) => { setSelectedDate(d); setSelectedTime(null); }}
                />
              </div>
              <div>
                {!selectedDate || !dateStr ? (
                  <p className="grid min-h-[180px] place-items-center rounded-2xl border border-dashed text-center text-sm text-muted-foreground/70">{t("booking.selectAppointment")}</p>
                ) : (
                  <TimeSlots
                    therapistId={therapistId} dateStr={dateStr} selectedDate={selectedDate}
                    selectedTime={selectedTime} enabled={!!selectedService} serviceId={selectedService?.id ?? null}
                    emergencyDay={isEmergency} emergencyPrice={emergency.data?.price ?? null}
                    onSelectTime={(time) => setSelectedTime(time)}
                  />
                )}
              </div>
            </div>
          )}

          {step === "modality" && (
            <>
              <h1 className="mb-4 text-2xl" style={{ fontFamily: "var(--font-heading)", fontWeight: 500 }}>{t("booking.modalityQuestion")}</h1>
              <ModalityStep selected={selectedModality} officeAddress={officeAddress} onSelect={(m) => setSelectedModality(m)} />
            </>
          )}

          {step === "payment" && (
            <PaymentStep
              selected={selectedPayment}
              onSelect={(m) => {
                setSelectedPayment(m);
                setStep(nextStep("payment", ctx));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              companyCredit={companyCreditOption}
              hideBankTransfer={isEmergency}
            />
          )}

          {step === "confirm" && pendingMonri && (
            <div data-testid="booking-pending-monri" className="flex flex-col gap-3 rounded-2xl border border-border bg-muted/40 p-4">
              <p className="text-sm font-semibold text-foreground">
                {t("booking.amountToPay", { amount: fmtKm(pendingMonri.amount) })}
              </p>
              <p className="text-[13px] text-muted-foreground">
                {t("booking.amountChangedBeforePay", { amount: fmtKm(pendingMonri.amount) })}
              </p>
              <Button data-testid="booking-continue-to-payment" onClick={() => submitMonriForm(pendingMonri.monri)}>
                {t("booking.continueToPayment")} <CaretRight className="size-4" />
              </Button>
            </div>
          )}

          {step === "confirm" && !pendingMonri && !isEmergency && selectedService?.maxMembers ? (
            <div className="mb-4">
              <MemberEmailsField
                value={memberEmails}
                onChange={setMemberEmails}
                maxExtra={maxExtraMembers(selectedService.maxMembers)}
              />
            </div>
          ) : null}
          {step === "confirm" && !pendingMonri && displayService && selectedDate && selectedTime && selectedPayment && (
            (isAuthenticated || justAuthenticated) ? (
              <ConfirmStep
                service={displayService} date={selectedDate} time={selectedTime}
                paymentMethod={selectedPayment}
                companyPayment={companyPayment}
                chargeAmount={clientChargeAmount}
                modality={effectiveModality} officeAddress={officeAddress}
              />
            ) : (
              <BookingAuthStep
                service={displayService} date={selectedDate} time={selectedTime} isBooking={isBooking}
                onAuthenticated={() => { void handleAuthenticatedAtConfirm(); }}
                paymentMethod={selectedPayment}
                companyPayment={companyPayment}
                chargeAmount={clientChargeAmount}
                modality={effectiveModality} officeAddress={officeAddress}
              />
            )
          )}
        </div>

      </div>

      {!isLast && (
        <aside className="hidden border-t lg:block">
          <button
            type="button"
            onClick={() => setDesktopSummaryOpen((o) => !o)}
            aria-expanded={desktopSummaryOpen}
            className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors hover:bg-muted/40"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {t("booking.summaryTitle")}
              <CaretDown className={`size-4 text-muted-foreground transition-transform ${desktopSummaryOpen ? "rotate-180" : ""}`} />
            </span>
            <span className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">{t("booking.totalLabel")}</span>
              <span className="text-lg" style={{ fontFamily: "var(--font-heading)", fontWeight: 600 }}>{total}</span>
            </span>
          </button>
          {desktopSummaryOpen && (
            <div className="px-5 pb-4">
              <BookingSummary data={summary} />
            </div>
          )}
        </aside>
      )}

      <div className="hidden items-center justify-between border-t px-5 py-3 lg:flex">
        <Button data-testid="booking-back" variant="ghost" size="sm" onClick={goBack} disabled={activeIndex === 0} className="text-muted-foreground">
          <CaretLeft className="size-4" /> {t("booking.back")}
        </Button>
        {showConfirmInFooter ? (
          <Button data-testid="booking-confirm" onClick={goNext} disabled={!advanceOk || isBooking}>
            {isBooking ? <CircleNotch className="size-4 animate-spin" /> : <CheckCircle className="size-4" />}
            {confirmLabel}
          </Button>
        ) : !isLast && (
          <Button data-testid="booking-continue" onClick={goNext} disabled={!advanceOk}>{t("booking.next")} <CaretRight className="size-4" /></Button>
        )}
      </div>

      <div className="sticky bottom-0 border-t bg-card/90 backdrop-blur lg:hidden">
        {!isLast && (
          <>
            <button onClick={() => setMobileSummaryOpen((o) => !o)} aria-expanded={mobileSummaryOpen} className="flex w-full justify-center pb-1 pt-2" aria-label={t("booking.summaryTitle")}>
              <span className="h-1 w-9 rounded-full bg-border" />
            </button>
            {mobileSummaryOpen && (
              <div className="max-h-[40vh] overflow-auto px-4 pb-2"><BookingSummary data={summary} /></div>
            )}
          </>
        )}
        <div className={`flex items-center gap-3 px-4 pb-4 ${isLast ? "pt-3" : "pt-1"}`}>
          {activeIndex > 0 && (
            <Button
              data-testid="booking-back-mobile"
              variant="outline"
              size="lg"
              onClick={goBack}
              className="h-12 rounded-xl px-5 text-[15px] font-medium text-muted-foreground"
            >
              <CaretLeft className="size-[18px]" weight="bold" /> {t("booking.back")}
            </Button>
          )}
          {showConfirmInFooter ? (
            <Button
              data-testid="booking-confirm-mobile"
              size="lg"
              onClick={goNext}
              disabled={!advanceOk || isBooking}
              className="h-12 flex-1 gap-1.5 rounded-xl text-[15px] font-semibold shadow-lg shadow-primary/25 transition-transform active:scale-[0.98]"
            >
              {isBooking ? <CircleNotch className="size-[18px] animate-spin" /> : <CheckCircle className="size-[18px]" weight="bold" />}
              {confirmLabel}
            </Button>
          ) : !isLast && (
            <Button
              data-testid="booking-continue-mobile"
              size="lg"
              onClick={goNext}
              disabled={!advanceOk}
              className="h-12 flex-1 gap-1.5 rounded-xl text-[15px] font-semibold shadow-lg shadow-primary/25 transition-transform active:scale-[0.98]"
            >
              {t("booking.next")} <CaretRight className="size-[18px]" weight="bold" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
